import React, { useEffect, useMemo, useRef } from "react";

import { FormSubscribe, PYForm } from "@performyard/py-components";

import {
	type ExternalFormData,
	ExternalReviewForm,
	Form360,
	type Form360Data,
	type StandardFormData,
	StandardReviewForm,
	buildForm360Schema,
	buildStandardFormSchema,
	externalFormSchema,
	type form360Schema,
	type standardFormSchema,
} from "../../../../../../components/FormSelectionCard";

import type { ConfiguredForm, FormDataByType, FormType } from "./types";

type FormCardProps<T extends FormType> = {
	formType: T;
	form: ConfiguredForm<T>;
	cycleStartDate: Date;
	companyTimezone: string;
	defaultDueTime: number;
	readOnly?: boolean;
	onChange: (localId: string, data: FormDataByType[T]) => void;
	onDelete?: (localId: string) => void;
	formIdAttr: string;
};

const StandardFormCard = ({
	form,
	cycleStartDate,
	companyTimezone,
	defaultDueTime,
	readOnly = false,
	onChange,
	onDelete,
	formIdAttr,
}: FormCardProps<"standard">) => {
	const handleDelete = onDelete ? () => onDelete(form.localId) : undefined;
	const schema = useMemo(() => buildStandardFormSchema(cycleStartDate), [cycleStartDate]);
	return (
		<PYForm<typeof standardFormSchema>
			id={formIdAttr}
			schema={schema}
			defaultValues={form.data}
			onSubmit={() => undefined}
		>
			<StandardReviewForm
				key="card"
				name={form.data.formName}
				fromTemplate={form.fromTemplate}
				onDelete={handleDelete}
				isRelativeDates={form.data.isRelativeDates}
				relativeDateStartDate={cycleStartDate}
				companyTimezone={companyTimezone}
				defaultDueTime={defaultDueTime}
				minimumDueDate={cycleStartDate}
				readOnly={readOnly}
				editableFields={readOnly ? [] : undefined}
			/>
			<SyncValues<StandardFormData> key="sync" localId={form.localId} onChange={onChange} />
		</PYForm>
	);
};

const Form360FormCard = ({
	form,
	cycleStartDate,
	defaultDueTime,
	readOnly = false,
	onChange,
	onDelete,
	formIdAttr,
}: FormCardProps<"360">) => {
	const handleDelete = onDelete ? () => onDelete(form.localId) : undefined;
	const schema = useMemo(() => buildForm360Schema(cycleStartDate), [cycleStartDate]);
	return (
		<PYForm<typeof form360Schema>
			id={formIdAttr}
			schema={schema}
			defaultValues={form.data}
			onSubmit={() => undefined}
		>
			<Form360
				key="card"
				name={form.data.formName}
				fromTemplate={form.fromTemplate}
				onDelete={handleDelete}
				minimumDueDate={cycleStartDate}
				defaultDueTime={defaultDueTime}
				readOnly={readOnly}
				editableFields={readOnly ? [] : undefined}
			/>
			<SyncValues<Form360Data> key="sync" localId={form.localId} onChange={onChange} />
		</PYForm>
	);
};

const ExternalFormCard = ({ form, readOnly = false, onChange, onDelete, formIdAttr }: FormCardProps<"external">) => {
	const handleDelete = onDelete ? () => onDelete(form.localId) : undefined;
	return (
		<PYForm<typeof externalFormSchema>
			id={formIdAttr}
			schema={externalFormSchema}
			defaultValues={form.data}
			onSubmit={() => undefined}
		>
			<ExternalReviewForm
				key="card"
				name={form.data.formName}
				fromTemplate={form.fromTemplate}
				onDelete={handleDelete}
				readOnly={readOnly}
				editableFields={readOnly ? [] : undefined}
			/>
			<SyncValues<ExternalFormData> key="sync" localId={form.localId} onChange={onChange} />
		</PYForm>
	);
};

export const FormCard = <T extends FormType>(props: FormCardProps<T>) => {
	switch (props.formType) {
		case "standard":
			return <StandardFormCard {...(props as unknown as FormCardProps<"standard">)} />;
		case "360":
			return <Form360FormCard {...(props as unknown as FormCardProps<"360">)} />;
		case "external":
			return <ExternalFormCard {...(props as unknown as FormCardProps<"external">)} />;
	}
};

/**
 * Subscribes to the surrounding PYForm's values and bubbles them up to the
 * parent reducer on every change. Lives inside PYForm so it can use FormSubscribe.
 */
const SyncValues = <T,>({ localId, onChange }: { localId: string; onChange: (id: string, data: T) => void }) => (
	<FormSubscribe id={`sync-${localId}`} selector={(state) => state.values as T}>
		{(values) => <SyncEffect localId={localId} values={values} onChange={onChange} />}
	</FormSubscribe>
);

const SYNC_DEBOUNCE_MS = 200;

const SyncEffect = <T,>({
	localId,
	values,
	onChange,
}: {
	localId: string;
	values: T;
	onChange: (id: string, data: T) => void;
}) => {
	// Hold onChange in a ref so re-renders that change its identity don't restart the debounce timer.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const lastSyncedRef = useRef<T>(values);
	const pendingRef = useRef<{ localId: string; values: T } | null>(null);

	useEffect(() => {
		if (lastSyncedRef.current === values) return;
		pendingRef.current = { localId, values };
		const handle = setTimeout(() => {
			onChangeRef.current(localId, values);
			lastSyncedRef.current = values;
			pendingRef.current = null;
		}, SYNC_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [localId, values]);

	// Flush any pending change on unmount so input isn't lost when the panel unmounts (tab switch, Next).
	useEffect(
		() => () => {
			if (pendingRef.current) {
				onChangeRef.current(pendingRef.current.localId, pendingRef.current.values);
			}
		},
		[]
	);

	return null;
};
